var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ModuleId, AskMessage, AskErrorMessage, LayoutUpdatedMessage } from "flavr-common";
import { Box3, Sphere } from "flavr-math";
import { LoadMeshMessage, MeshLoadedMessage } from "@messages/rendering/RendererMessages";
import { RequestMeshGeometryMessage, ResponseMeshGeometryMessage } from "@messages/mesh/MeshMessages";
import { assertIsWeb } from "@messages/environment";
import { Module } from "@modules/module";
import { meshBoundingService } from "@modules/bounds";
import { meshLoaderService } from "./MeshLoaderService";
import { imageLoaderService } from "../image/ImageLoaderService";
import { applyBarycentricCoordinatesToGroup } from "../shader/BarycentricWireframe";
assertIsWeb("MeshLoaderModule");
/**
 * Centralizes asynchronous mesh loading so modules can request geometry without owning file parsing or mesh cache management.
 *
 * Messages:
 * - `LoadMeshMessage` - Requests asynchronous loading of a named mesh asset (optionally a single piece within it), applying cached bounding and barycentric-coordinate data before answering with the loaded group.
 * - `RequestMeshGeometryMessage` - Loads a mesh (or one of its pieces) and answers with its raw vertex, index and matrix data.
 * - `LayoutUpdatedMessage` - Clears the mesh and image caches after room content changes.
 */
export class MeshLoaderModule extends Module {
    /**
     * Registers the subscriptions for mesh loading, mesh geometry requests and layout-change cache invalidation.
     */
    constructor() {
        super(ModuleId.MESH_LOADER);
        this.meshesWhichHaveBarycentricCoordinates = new Set();
        this.handleLoadMeshMessage = (message) => __awaiter(this, void 0, void 0, function* () {
            const msg = AskMessage.is(message) ? message.message : message;
            const askId = AskMessage.is(message) ? message.askId : undefined;
            const origin = (msg.meshPathAndName.startsWith("@")) ? "MinIO" : "Assets";
            if (msg.pieceName !== undefined)
                console.debug(`[renderer] Loading piece "${msg.pieceName}" in mesh "${msg.meshPathAndName}" from ${origin}`);
            else
                console.debug(`[renderer] Loading mesh "${msg.meshPathAndName}" from ${origin}`);
            try {
                const group = yield this.loadMeshWithRetry(msg);
                this.takeCareOfBounding(msg, group);
                this.takeCareOfBarycentrics(msg, group);
                if (askId !== undefined) {
                    this.connection.send(new MeshLoadedMessage(msg.meshPathAndName, group, msg.pieceName), askId);
                }
            }
            catch (error) {
                void this.onError(msg.meshPathAndName, askId);
            }
        });
        this.handleRequestMeshGeometryMessage = (msg, askId) => __awaiter(this, void 0, void 0, function* () {
            let group = yield meshLoaderService.load(msg.url);
            if (msg.pieceName) {
                group = meshLoaderService.findPieceObject(group, msg.pieceName);
            }
            // For collision requests targeting a specific piece, only use that object's own mesh geometry.
            // Descendant geometry belongs to separate child models and should only be aggregated by Compound shapes.
            const traverseDescendants = msg.pieceName === undefined;
            const result = meshLoaderService.verticesFromMesh(group, traverseDescendants);
            this.connection.answer(askId, new ResponseMeshGeometryMessage(msg.url, result.vertices, result.indexes, result.matrices));
        });
        this.handleLayoutUpdatedMessage = () => {
            meshLoaderService.clearCache();
            // ToDo: move to a better place
            imageLoaderService.clearCache();
        };
        this.connection.subscribeByType(LoadMeshMessage, this.handleLoadMeshMessage);
        this.connection.subscribeForAskRequest(RequestMeshGeometryMessage, this.handleRequestMeshGeometryMessage);
        this.connection.subscribeByType(LayoutUpdatedMessage, this.handleLayoutUpdatedMessage);
    }
    /**
     * Keeps the standard module life-cycle entry point even though mesh loading subscriptions are already registered in the constructor.
     */
    init() { }
    loadMeshWithRetry(msg) {
        return __awaiter(this, void 0, void 0, function* () {
            const retryDelaysMs = [0, 200, 1000, 2000];
            let lastError;
            for (let attempt = 0; attempt < retryDelaysMs.length; attempt += 1) {
                try {
                    if (retryDelaysMs[attempt] > 0) {
                        yield this.sleep(retryDelaysMs[attempt]);
                    }
                    return yield this.loadMeshOrPiece(msg);
                }
                catch (error) {
                    lastError = error;
                    const currentAttempt = attempt + 1;
                    const maxAttempts = retryDelaysMs.length;
                    console.warn(`[renderer] Mesh load attempt ${currentAttempt}/${maxAttempts} failed for "${msg.meshPathAndName}"`, error);
                }
            }
            throw lastError;
        });
    }
    loadMeshOrPiece(msg) {
        return __awaiter(this, void 0, void 0, function* () {
            let group = yield meshLoaderService.load(msg.meshPathAndName);
            if (msg.pieceName) {
                group = meshLoaderService.findPieceObject(group, msg.pieceName);
            }
            return group;
        });
    }
    takeCareOfBounding(msg, group) {
        const url = meshBoundingService.generateFullMeshUrl(msg.meshPathAndName, msg.pieceName);
        const aabb = new Box3();
        const boundingSphere = new Sphere();
        meshLoaderService.computeGroupBounding(group, aabb, boundingSphere);
        meshBoundingService.updateBounding(url, aabb, boundingSphere);
    }
    takeCareOfBarycentrics(msg, group) {
        if (!msg.addBarycentricCoordinates) {
            return;
        }
        if (this.meshesWhichHaveBarycentricCoordinates.has(msg.meshPathAndName)) {
            return;
        }
        this.meshesWhichHaveBarycentricCoordinates.add(msg.meshPathAndName);
        applyBarycentricCoordinatesToGroup(group);
    }
    onError(meshName, askId) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (askId !== undefined) {
                    const group = yield meshLoaderService.load("meshes/error.glb");
                    this.connection.send(new MeshLoadedMessage(meshName, group), askId);
                }
            }
            catch (_a) {
                if (askId !== undefined) {
                    this.connection.send(new AskErrorMessage(`Can't load mesh ${meshName}`), askId);
                }
            }
        });
    }
    sleep(ms) {
        return __awaiter(this, void 0, void 0, function* () {
            yield new Promise((resolve) => {
                setTimeout(resolve, ms);
            });
        });
    }
}
